import { Component, Host, Prop, State, Watch, h } from '@stencil/core';
import { apiUrl } from '../../constants';
import { format } from 'date-fns';
import { Subscription } from '../../../../utilities/types/subscription';
import { License } from '../../../../utilities/types/license';

type IsSubmittingState = {
  cancelSubNow: boolean;
  cancelSubPeriodEnd: boolean;
  cancelLicense: boolean;
  reactivateSub: boolean;
};

@Component({
  tag: 'salable-cancellation-button',
  styleUrl: 'salable-cancellation-button.css',
  shadow: true,
})
export class SalableCancellationButton {
  @State() errorMessage: string | null = null;
  @State() successMessage:
    | ({
        message: string;
      } & ({ type: 'REACTIVATE' } | ({ type: 'CANCEL' } & ({ when: 'NOW' } | { when: 'END'; date: string }))))
    | null = null;
  @State() apiUrl: string | null = apiUrl;
  @State() state: Subscription | License | null = null;
  @State() isSubmittingState: IsSubmittingState = {
    cancelSubNow: false,
    cancelSubPeriodEnd: false,
    cancelLicense: false,
    reactivateSub: false,
  };

  /**
   * The generated token for this session from the Salable API
   **/
  @Prop() sessionToken!: string;
  /**
   * The uuid of the license or subscription to cancel
   **/
  @Prop() uuid!: string;
  // eslint-disable-next-line @stencil-community/required-jsdoc
  @Prop() environment?: string;

  @Watch('sessionToken')
  @Watch('uuid')
  validateProp(newValue: string, propName: string) {
    if (typeof newValue !== 'string' || newValue.trim() === '') {
      console.error(`${propName} is a required property and cannot be empty`);
      this.errorMessage = 'Failed to load Cancellation Button';
    }
  }

  private getSvg(type: 'CANCEL' | 'REACTIVATE' | 'CHECK') {
    switch (type) {
      case 'CANCEL':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="9" height="10" viewBox="0 0 9 10" fill="none">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.60001 4.99403C1.60324 3.50561 2.81082 2.30001 4.29999 2.30001C4.74182 2.30001 5.10001 1.94184 5.10001 1.50002C5.10001 1.05818 4.74182 0.700012 4.29999 0.700012C1.92517 0.700012 0 2.62519 0 5.00002C3.05176e-05 5.44184 0.358215 5.80001 0.800049 5.80001C1.24185 5.80001 1.60004 5.44184 1.60001 5.00002V4.99403ZM7 5.00002C7 6.49118 5.79117 7.70001 4.29999 7.70001C3.85818 7.70001 3.5 8.05818 3.5 8.50002C3.5 8.94184 3.85818 9.30001 4.29999 9.30001C6.67221 9.30001 8.59579 7.37907 8.60001 5.00784V5.00002C8.60004 4.55818 8.24185 4.20001 7.80005 4.20001C7.35822 4.20001 7.00003 4.55818 7 5.00002ZM7.36569 3.0657C7.6781 2.75328 7.6781 2.24675 7.36569 1.93433C7.05327 1.62191 6.54673 1.62191 6.23431 1.93433L1.23431 6.93433C0.921895 7.24675 0.921895 7.75328 1.23431 8.0657C1.54673 8.37812 2.05327 8.37812 2.36569 8.0657L7.36569 3.0657Z"
              fill="currentColor"
            />
          </svg>
        );
      case 'REACTIVATE':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="9" height="10" viewBox="0 0 9 10" fill="none">
            <path
              d="M8 4.99939C8 6.93239 6.433 8.49939 4.5 8.49939C2.567 8.49939 1 6.93239 1 4.99939C1 3.06639 2.567 1.49939 4.5 1.49939"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />
            <circle cx="4.5" cy="5.00064" r="3.5" stroke="#2F60FF" stroke-width="1.6" stroke-linecap="round" stroke-dasharray="0.3 2.2" />
          </svg>
        );
      case 'CHECK':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M9.13137 1.61617L3.23235 7.51519L0 4.28284L1.13137 3.15147L3.23235 5.25245L8 0.484802L9.13137 1.61617Z"
              fill="currentColor"
            />
          </svg>
        );
    }
  }

  private validateProps() {
    this.validateProp(this.sessionToken, 'sessionToken');
    this.validateProp(this.uuid, 'uuid');
  }

  private async fetchWrapper(url: string, method: 'GET' | 'PUT' | 'DELETE' = 'GET') {
    return fetch(url, { method, headers: { version: 'v2', Authorization: `Bearer ${this.sessionToken}` } });
  }

  private async fetchUuid() {
    const getSubUrl = `${this.apiUrl}/subscriptions/${this.uuid}`;
    const getLicenseUrl = `${this.apiUrl}/licenses/${this.uuid}`;

    const [subResponse, licenseResponse] = await Promise.all([this.fetchWrapper(getSubUrl), this.fetchWrapper(getLicenseUrl)]);

    const subStatus = subResponse.status;
    const licenseStatus = licenseResponse.status;

    if (subResponse.ok) {
      const data = (await subResponse.json()) as Subscription;
      this.state = data;
      return;
    }

    if (licenseResponse.ok) {
      const data = (await licenseResponse.json()) as License;
      this.state = data;
      return;
    }

    if ([401, 403].includes(subStatus) && [401, 403].includes(licenseStatus)) {
      this.errorMessage = 'Unauthorised';
      return null;
    }

    if (subStatus === 404 && licenseStatus === 404) {
      this.errorMessage = 'License/subscription not found';
      return null;
    }

    this.errorMessage = 'Failed to load Cancellation Button';
    return null;
  }

  private isSubscription(data: Subscription | License): data is Subscription {
    return (data as Subscription)?.paymentIntegrationSubscriptionId !== undefined;
  }

  private async reactivateSubscription() {
    if (!this.isSubscription(this.state)) return;
    this.isSubmittingState = {
      ...this.isSubmittingState,
      reactivateSub: true,
    };

    const response = await this.fetchWrapper(`${this.apiUrl}/subscriptions/${this.uuid}/reactivate`, 'PUT');

    this.isSubmittingState = {
      ...this.isSubmittingState,
      reactivateSub: false,
    };

    if (response.ok) {
      this.successMessage = {
        message: 'Subscription successfully reactivated',
        type: 'REACTIVATE',
      };
      return;
    }

    if ([401, 403].includes(response.status)) {
      this.errorMessage = 'Unauthorised';
      return null;
    }

    if (response.status === 404) {
      this.errorMessage = 'Subscription not found';
      return null;
    }

    this.errorMessage = 'Failed to reactivate subscription';
    return null;
  }

  private async cancelSubscription(when: 'end' | 'now') {
    if (!this.isSubscription(this.state)) return;

    this.isSubmittingState = {
      ...this.isSubmittingState,
      ...(when === 'end' && { cancelSubPeriodEnd: true }),
      ...(when === 'now' && { cancelSubNow: true }),
    };

    const response = await this.fetchWrapper(`${this.apiUrl}/subscriptions/${this.uuid}/cancel?when=${when}`, 'PUT');

    this.isSubmittingState = {
      ...this.isSubmittingState,
      ...(when === 'end' && { cancelSubPeriodEnd: false }),
      ...(when === 'now' && { cancelSubNow: false }),
    };

    if (response.ok) {
      this.successMessage = {
        message: when === 'now' ? 'Subscription successfully cancelled' : 'Subscription will be cancelled on',
        type: 'CANCEL',
        when: when === 'now' ? 'NOW' : 'END',
        ...(when !== 'now' && { date: this.formatDate(this.state.expiryDate) }),
      };
      return;
    }

    if ([401, 403].includes(response.status)) {
      this.errorMessage = 'Unauthorised';
      return null;
    }

    if (response.status === 404) {
      this.errorMessage = 'Subscription not found';
      return null;
    }

    this.errorMessage = 'Failed to cancel subscription';
    return null;
  }

  private async cancelLicense() {
    if (this.isSubscription(this.state)) return;
    this.isSubmittingState = {
      ...this.isSubmittingState,
      cancelLicense: true,
    };

    const response = await this.fetchWrapper(`${this.apiUrl}/licenses/${this.uuid}`, 'DELETE');

    this.isSubmittingState = {
      ...this.isSubmittingState,
      cancelLicense: false,
    };

    if (response.ok) {
      this.successMessage = {
        message: 'License successfully cancelled',
        type: 'CANCEL',
        when: 'NOW',
      };
      return;
    }

    if ([401, 403].includes(response.status)) {
      this.errorMessage = 'Unauthorised';
      return null;
    }

    if (response.status === 404) {
      this.errorMessage = 'License not found';
      return null;
    }

    this.errorMessage = 'Failed to cancel license';
    return null;
  }

  private formatDate(date: string) {
    return format(new Date(date), 'do MMM YYY');
  }

  async componentWillLoad() {
    this.validateProps();
    if (this.errorMessage) return;

    this.apiUrl = this.environment === 'preview' ? 'https://api.salable.org' : apiUrl;

    try {
      await this.fetchUuid();
    } catch (e) {
      this.errorMessage = 'Failed to load Cancellation Button';
      return;
    }
  }

  render() {
    const isTestMode = this.sessionToken?.startsWith('sess_test_');

    const buttonBaseStyles = 'flex justify-center items-center border rounded-sm px-3 py-2.5 disabled:animate-pulse ease-in-out duration-150 transition-colors bg-white';
    const buttonCancelStyles = `${buttonBaseStyles} text-[#ED0000] hover:bg-[#FFF3F3] border-[#ED0000]`;

    return (
      <Host>
        <div class="font-sans bg-white rounded-xl shadow-sm overflow-hidden dark:bg-slate-900 w-max text-sm">
          {isTestMode ? (
            <div class="border-solid border-t-4 border-orange-500 w-full flex justify-center">
              <p class="px-1 bg-orange-500 rounded-b font-bold text-white uppercase text-xs">test mode</p>
            </div>
          ) : null}

          {this.errorMessage ? (
            <div class="p-2">
              <p class="bg-[#750724] text-white py-2.5 px-3 rounded-sm leading-none font-bold" data-testid={`salable-cancellation-button-error`}>
                ! {this.errorMessage}
              </p>
            </div>
          ) : null}

          {this.successMessage ? (
            <div class="p-2">
              <div class="flex gap-x-1 items-center justify-center px-3 py-2.5 bg-white rounded-sm">
                <span class="mr-1">{this.getSvg(this.successMessage.type === 'REACTIVATE' ? 'CHECK' : 'CANCEL')}</span>
                {this.successMessage.type === 'REACTIVATE' || this.successMessage.when === 'NOW' ? (
                  <p>{this.successMessage.message}</p>
                ) : (
                  <p>
                    {this.successMessage.message} <span class="font-bold ml-1">{this.successMessage.date}</span>
                  </p>
                )}
              </div>
            </div>
          ) : null}

          {!this.successMessage && (this.state?.status === 'CANCELED' || (this.state && this.isSubscription(this.state) && this.state?.cancelAtPeriodEnd)) ? (
            <div class="p-2 flex gap-2" data-testid="salable-cancellation-button-already-cancelled">
              {this.state && !this.isSubscription(this.state) ? (
                <div class="flex gap-x-1 items-center justify-center px-3 py-2.5 bg-white rounded-sm">
                  <span class="mr-1">{this.getSvg('CANCEL')}</span>
                  <p class="font-bold">Your license is cancelled.</p>
                </div>
              ) : this.isSubscription(this.state) && this.state.cancelAtPeriodEnd ? (
                <div class="flex gap-x-1 items-center justify-center px-3 py-2.5 bg-white rounded-sm">
                  <p>
                    Your subscription will be cancelled on <span class="font-bold">{this.formatDate(this.state.expiryDate)}.</span>
                  </p>
                </div>
              ) : (
                <div class="flex gap-x-1 items-center justify-center px-3 py-2.5 bg-white rounded-sm">
                  <span class="mr-1">{this.getSvg('CANCEL')}</span>
                  <p class="font-bold">Your subscription is cancelled.</p>
                </div>
              )}

              {this.state && this.isSubscription(this.state) && this.state.cancelAtPeriodEnd ? (
                <button
                  onClick={() => this.reactivateSubscription()}
                  class={`${buttonBaseStyles} font-bold border-[#2F60FF] text-[#2F60FF] hover:bg-[#D6EFFF]`}
                  disabled={this.isSubmittingState.reactivateSub}
                >
                  <span class="mr-1">{this.getSvg('REACTIVATE')}</span>
                  Reactivate Subscription
                </button>
              ) : null}
            </div>
          ) : null}

          {!this.errorMessage && !this.successMessage && this.state.status !== 'CANCELED' && (!this.state.cancelAtPeriodEnd || (this.state && !this.isSubscription(this.state))) ? (
            <div class="text-gray-800 dark:text-white font-medium text-center p-2">
              {this.state && this.isSubscription(this.state) ? (
                <div class="flex gap-2">
                  <button class={`${buttonCancelStyles} font-bold`} onClick={() => this.cancelSubscription('now')} disabled={this.isSubmittingState.cancelSubNow}>
                    <span class="mr-1">{this.getSvg('CANCEL')}</span>
                    Cancel subscription now
                  </button>
                  <button class={buttonCancelStyles} onClick={() => this.cancelSubscription('end')} disabled={this.isSubmittingState.cancelSubPeriodEnd}>
                    <span class="mr-1">{this.getSvg('CANCEL')}</span>
                    Cancel subscription on <span class="font-bold ml-1">{this.formatDate(this.state.expiryDate)}</span>
                  </button>
                </div>
              ) : (
                <button class={`${buttonCancelStyles} font-bold`} onClick={() => this.cancelLicense()} disabled={this.isSubmittingState.cancelLicense}>
                  <span class="mr-1">{this.getSvg('CANCEL')}</span>
                  Cancel license now
                </button>
              )}
            </div>
          ) : null}
        </div>
      </Host>
    );
  }
}
